import { companyPortfolioAsset } from "@/_core/data/DataModel";

const PRODUCT_QUERY_KEY = { 0: "currencies", 1: "commodities" };
const parseList = val => (val ? String(val).split(",") : []);

const HEDGING_PROGRAM_SHEET = 2;

export const dataModelRouteSync = {
  data() {
    return {
      isApplyingDataRoute: false,
      programSelection: null,
      dataLoadToken: 0
    };
  },
  watch: {
    $route(to, from) {
      if (this._isDestroyed || this._isBeingDestroyed) return;
      if (this.isApplyingDataRoute) return;

      const pathChanged =
        !from ||
        to.name !== from.name ||
        (to.params &&
          from.params &&
          to.params.productType !== from.params.productType);
      if (pathChanged) {
        this.syncDataRoute(true);
        return;
      }

      if (this.isSameDataQuery(this.buildDataQuery())) return;
      this.applyDataRoute(this.$route);
    }
  },
  methods: {
    isDataPathCanonical() {
      return /^data-/.test(this.$route.name || "");
    },
    dataProductKey() {
      return PRODUCT_QUERY_KEY[this.selectedNavAsset] || "currencies";
    },
    dataProductOptions() {
      return this.selectedNavAsset === 0 ? this.currencies : this.commodities;
    },
    dataSelectedProduct() {
      return this.selectedNavAsset === 0
        ? this.selectedCurrency
        : this.selectedCommodity;
    },
    setDataSelectedProduct(list) {
      if (this.selectedNavAsset === 0) {
        this.selectedCurrency = list;
        this.prevSelectedCurrency = list;
      } else {
        this.selectedCommodity = list;
        this.prevSelectedCommodity = list;
      }
    },
    buildDataQuery() {
      const query = {};
      const company = this.companies && this.companies[this.selectedCompany];
      if (company && company.company_name != null) {
        query.company = String(company.company_name);
      }
      const singleSelect = this.selectedSheet === HEDGING_PROGRAM_SHEET;
      if (singleSelect && this.programSelection) {
        const { portfolio, product } = this.programSelection;
        if (portfolio != null && String(portfolio).length) {
          query.portfolios = String(portfolio);
        }
        if (product != null && String(product).length) {
          query[this.dataProductKey()] = String(product).toLowerCase();
        }
        return query;
      }
      if (this.selectedPortfolios && this.selectedPortfolios.length) {
        let ports = this.selectedPortfolios.filter(p => p && p.name != null);
        if (singleSelect) ports = ports.slice(0, 1);
        const names = ports.map(p => String(p.name));
        if (names.length) query.portfolios = names.join(",");
      }
      const products = this.dataSelectedProduct();
      if (products && products.length) {
        let prods = products.filter(p => p && p.name != null);
        if (singleSelect) prods = prods.slice(0, 1);
        const names = prods.map(p => String(p.name).toLowerCase());
        if (names.length) query[this.dataProductKey()] = names.join(",");
      }
      return query;
    },
    isSameDataQuery(query) {
      const current = this.$route.query || {};
      const keys = new Set([
        ...Object.keys(current),
        ...Object.keys(query)
      ]);
      for (const key of keys) {
        if ((current[key] || null) !== (query[key] || null)) return false;
      }
      return true;
    },
    onDataProgramSelection(sel) {
      this.programSelection = sel || null;
      if (this._isDestroyed || this._isBeingDestroyed) return;
      if (this.selectedSheet !== HEDGING_PROGRAM_SHEET) return;
      this.syncDataRoute(true);
    },
    syncDataRoute(replace = false) {
      if (this._isDestroyed || this._isBeingDestroyed) return;
      if (this.isApplyingDataRoute) return;
      const query = this.buildDataQuery();
      if (this.isSameDataQuery(query)) return;
      const target = {
        name: this.$route.name,
        params: this.$route.params,
        query
      };
      const navigation = replace
        ? this.$router.replace(target)
        : this.$router.push(target);
      navigation.catch(err => {
        if (err && err.name !== "NavigationDuplicated") {
          console.error("Error updating data-model route:", err);
        }
      });
    },
    dataRouteHasSelection(route) {
      const query = (route && route.query) || {};
      return (
        query.company != null ||
        query.portfolios != null ||
        query.currencies != null ||
        query.commodities != null
      );
    },
    applyDataRoute(route) {
      if (this._isDestroyed || this._isBeingDestroyed) return;
      const query = (route && route.query) || {};
      const hasSelection = this.dataRouteHasSelection(route);
      if (!hasSelection) {
        if (this.isDataPathCanonical()) this.syncDataRoute(true);
        return;
      }
      this.isApplyingDataRoute = true;
      try {
        // 1) Company (single): match by name back to the internal index id.
        if (query.company != null) {
          const wanted = String(query.company).toLowerCase();
          const idx = this.companies.findIndex(
            c => c && String(c.company_name).toLowerCase() === wanted
          );
          if (idx > -1) {
            this.selectedCompany = this.companies[idx].id;
            if (this.datamodel_preferences) {
              this.datamodel_preferences.selected_company = this.selectedCompany;
            }
          }
        }
        // Populate portfolios for the selected company (resets the selection).
        companyPortfolioAsset(this, 0, true, false);

        // 2) Portfolios (multi): match by name, case-insensitive.
        const portNames = parseList(query.portfolios).map(s => s.toLowerCase());
        if (portNames.length) {
          const matched = this.portfolios.filter(p =>
            p && portNames.includes(String(p.name).toLowerCase())
          );
          if (matched.length) {
            this.selectedPortfolios = matched;
            this.prevSelectedPortfolios = matched;
          }
        }
        // Populate products for the selected portfolios (no fetch yet).
        companyPortfolioAsset(this, 1, true, false);

        // 3) Product (multi): match by name, case-insensitive.
        const prodNames = parseList(query[this.dataProductKey()]).map(s =>
          s.toLowerCase()
        );
        if (prodNames.length) {
          const options = this.dataProductOptions() || [];
          const matched = options.filter(o =>
            prodNames.includes(String(o.name).toLowerCase())
          );
          if (matched.length) this.setDataSelectedProduct(matched);
        }
        // Final step: the full selection is now applied, so issue the single
        // authoritative expo/hedge fetch for this route.
        companyPortfolioAsset(this, 2, true, true);
      } catch (error) {
        console.error("Error applying data-model route:", error);
      } finally {
        this.isApplyingDataRoute = false;
      }
      this.syncDataRoute(true);
    }
  }
};
